import React, { useRef, useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import isMobileLib from 'is-mobile';
import EDButton from '../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../BaseComponents/Icon/EDIcon';
import EDDialog from '../../../../../BaseComponents/Dialog/EDDialog';
import ZoomableImageView from '../../../../../Components/ZoomableImage/ZoomableImageView';

// Android Chrome öppnar galleriet direkt istället för native-menyn med
// kamera/galleri-val. Detta beror på att Chrome blockerar programmatisk
// .click() på filinputs om den inte sker i direkt följd av en tillräckligt
// "djup" användarinteraktion — ett vanligt knapptryck räcker inte. Övriga
// webbläsare (iOS Safari, Samsung Internet m.fl.) visar native-menyn som
// förväntat. Accepterat beteende tills vidare.

interface IProps {
  onSave: (file: File) => void;
  dialogTitle?: string;
  disabled?: boolean;
}

const useStyles = makeStyles()({
  previewImg: {
    objectFit: 'contain',
    objectPosition: '50% 50%',
    height: 'inherit',
    width: 'inherit',
  },
  // display: none blockerar programmatisk .click() i Chrome på Android
  hiddenInput: {
    position: 'absolute',
    opacity: 0,
    width: 0,
    height: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
});

const isMobileOrTablet = isMobileLib({ tablet: true, featureDetect: true });

const BildVäljare: React.FC<IProps> = ({
  onSave,
  dialogTitle = 'Granska foto',
  disabled = false,
}) => {
  const { classes } = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleButtonClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setPreviewUrl(URL.createObjectURL(file));
      setPendingFile(file);
      setPreviewOpen(true);
      e.target.value = '';
    },
    []
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewOpen(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPendingFile(null);
  }, [previewUrl]);

  const handleSave = useCallback(() => {
    if (pendingFile) onSave(pendingFile);
    handlePreviewClose();
  }, [pendingFile, handlePreviewClose, onSave]);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={
          isMobileOrTablet ? 'image/*' : 'image/jpeg,image/png,image/webp'
        }
        className={classes.hiddenInput}
        onChange={handleFileChange}
      />

      <EDButton
        flat
        label="LÄGG TILL FOTO"
        tooltip="Lägg till foto"
        size="small"
        disabled={disabled}
        onClick={handleButtonClick}
        icon={
          <EDIcon
            iconType="image"
            marginRight={0.5}
            color="primary"
            size="small"
          />
        }
      />

      <EDDialog
        title={dialogTitle}
        open={previewOpen}
        onClose={handlePreviewClose}
        fullHeight
        footerActions={[
          { label: 'Avvisa', onClick: handlePreviewClose },
          { label: 'Spara', onClick: handleSave },
        ]}
      >
        {previewUrl && (
          <ZoomableImageView
            isImage
            title={dialogTitle}
            imageCount={1}
            imageIndex={0}
            noFullscreenToggle={false}
            extraBottomPadding={false}
            showCenteredImageToggle={false}
            useWheelToScroll={false}
            previousImage={() => {}}
            nextImage={() => {}}
            navHeader={dialogTitle}
          >
            <img
              className={classes.previewImg}
              src={previewUrl}
              alt="Förhandsgranskning"
            />
          </ZoomableImageView>
        )}
      </EDDialog>
    </>
  );
};

export default BildVäljare;
